'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import { Clock, Phone, CalendarX, Euro, ArrowRight } from 'lucide-react';

/**
 * Zeit-Spar-Rechner — interaktiver Linkbait-Calculator (i18n, 7 Locales).
 *
 * Kund:innen-Input: Termine/Tag, Minuten Telefon-Aufwand pro Termin,
 * Öffnungstage/Woche, No-Show-Rate, Ø-Behandlungswert, Stundenlohn.
 * Output: gesparte Admin-Stunden/Monat + € Zeitwert + zurückgewonnener
 * No-Show-Umsatz/Monat — alles client-side, kein Tracking.
 *
 * Annahmen (konservativ, transparent ausgewiesen):
 *  - Online-Buchung eliminiert ~70 % der Telefon-Termin-Zeit
 *  - Anzahlung + Erinnerungen senken No-Show-Rate auf ~5 %
 */

const WEEKS_PER_MONTH = 4.33;
const PHONE_TIME_REDUCTION = 0.7;
const RESIDUAL_NOSHOW_RATE = 0.05;

// Intl currency locale per app locale (EUR throughout — only formatting differs)
const CURRENCY_LOCALE: Record<string, string> = {
  de: 'de-DE',
  en: 'en-IE',
  es: 'es-ES',
  fr: 'fr-FR',
  ru: 'ru-RU',
  tr: 'tr-TR',
  ar: 'ar',
};

function NumberField({
  label,
  icon,
  value,
  setValue,
  min,
  max,
  step = 1,
  suffix,
}: {
  label: string;
  icon: React.ReactNode;
  value: number;
  setValue: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <span className="text-blue-600">{icon}</span>
        {label}
      </label>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-blue-600"
        />
        <div className="flex w-24 items-center gap-1 rounded-lg border border-gray-200 px-2 py-1.5">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            className="w-full bg-transparent text-right text-sm font-semibold text-gray-900 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
          />
          {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
        </div>
      </div>
    </div>
  );
}

export function TimeSavingsCalculator() {
  const t = useTranslations('calculator');
  const locale = useLocale();
  const [perDay, setPerDay] = useState(8);
  const [phoneMin, setPhoneMin] = useState(6);
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [noShowRate, setNoShowRate] = useState(18);
  const [avgValue, setAvgValue] = useState(60);
  const [hourlyWage, setHourlyWage] = useState(25);

  const eur = useMemo(() => {
    const fmt = new Intl.NumberFormat(CURRENCY_LOCALE[locale] ?? 'de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    });
    return (n: number) => fmt.format(Math.round(n));
  }, [locale]);

  const result = useMemo(() => {
    const apptsPerMonth = perDay * daysPerWeek * WEEKS_PER_MONTH;
    const phoneMinutesNow = apptsPerMonth * phoneMin;
    const savedHours = (phoneMinutesNow * PHONE_TIME_REDUCTION) / 60;
    const savedTimeValue = savedHours * hourlyWage;

    const currentNoShowRate = noShowRate / 100;
    const reducedRate = Math.min(currentNoShowRate, RESIDUAL_NOSHOW_RATE);
    const recoveredAppts = apptsPerMonth * Math.max(0, currentNoShowRate - reducedRate);
    const recoveredRevenue = recoveredAppts * avgValue;

    const totalMonthly = savedTimeValue + recoveredRevenue;
    return {
      apptsPerMonth: Math.round(apptsPerMonth),
      savedHours: Math.round(savedHours),
      savedTimeValue,
      recoveredAppts: Math.round(recoveredAppts),
      recoveredRevenue,
      totalMonthly,
      totalYearly: totalMonthly * 12,
    };
  }, [perDay, phoneMin, daysPerWeek, noShowRate, avgValue, hourlyWage]);

  return (
    <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[1.1fr_1fr]">
      {/* Inputs */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-gray-900">{t('inputsTitle')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('inputsSub')}</p>
        <div className="mt-6 space-y-6">
          <NumberField label={t('fields.perDay')} icon={<Clock className="h-4 w-4" />} value={perDay} setValue={setPerDay} min={1} max={40} />
          <NumberField label={t('fields.phoneMin')} icon={<Phone className="h-4 w-4" />} value={phoneMin} setValue={setPhoneMin} min={1} max={20} suffix={t('minSuffix')} />
          <NumberField label={t('fields.daysPerWeek')} icon={<Clock className="h-4 w-4" />} value={daysPerWeek} setValue={setDaysPerWeek} min={1} max={7} />
          <NumberField label={t('fields.noShowRate')} icon={<CalendarX className="h-4 w-4" />} value={noShowRate} setValue={setNoShowRate} min={0} max={40} suffix="%" />
          <NumberField label={t('fields.avgValue')} icon={<Euro className="h-4 w-4" />} value={avgValue} setValue={setAvgValue} min={10} max={300} step={5} suffix="€" />
          <NumberField label={t('fields.hourlyWage')} icon={<Euro className="h-4 w-4" />} value={hourlyWage} setValue={setHourlyWage} min={10} max={120} step={5} suffix="€" />
        </div>
      </div>

      {/* Result — sticky on desktop */}
      <div className="flex flex-col gap-4 lg:sticky lg:top-24 lg:self-start">
        <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 p-6 text-white sm:p-8">
          <p className="text-sm font-medium text-blue-100">{t('result.label')}</p>
          <div className="mt-2 flex flex-wrap items-baseline gap-2">
            <span className="text-4xl font-bold tracking-tight tabular-nums sm:text-5xl">
              {eur(result.totalMonthly)}
            </span>
            <span className="text-blue-100">{t('result.perMonth')}</span>
          </div>
          <p className="mt-1 text-sm text-blue-100 tabular-nums">
            {t('result.perYear', { value: eur(result.totalYearly) })}
          </p>

          <div className="mt-6 space-y-3 border-t border-white/20 pt-5 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-blue-100">{t('result.savedTimeLabel')}</span>
              <span className="text-right font-semibold tabular-nums">
                {t('result.savedTimeValue', { hours: result.savedHours, value: eur(result.savedTimeValue) })}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-blue-100">{t('result.recoveredLabel')}</span>
              <span className="text-right font-semibold tabular-nums">
                {t('result.recoveredValue', { appts: result.recoveredAppts, value: eur(result.recoveredRevenue) })}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-blue-200">
              <span>{t('result.basisLabel')}</span>
              <span className="tabular-nums">{result.apptsPerMonth}</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <p
            className="text-sm text-gray-600 [&_b]:font-semibold [&_b]:text-gray-900"
            dangerouslySetInnerHTML={{ __html: t.raw('pricingNote') as string }}
          />
          <Link
            href="/register"
            className="mt-4 inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-gray-950 px-6 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
          >
            {t('ctaButton')}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-center text-xs text-gray-400">{t('ctaSub')}</p>
        </div>

        <p className="px-2 text-xs leading-relaxed text-gray-400">{t('assumptions')}</p>
      </div>
    </div>
  );
}
